import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { batch } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import { createSelector } from "reselect";
import Cookies from "js-cookie";

import { getCurrentUserSellers } from './redux/sellers/actions';
import { getUserRegistrationSteps, getUserSettings, refresh, setUserCurrency } from "redux/users/actions";
import { getAndSetSellerById } from './redux/sellers/actions';
import { Header, PostMessageCatcher, Sidebar } from "components";
import { AlertContainer } from "components/Alerts";
import FormSentContainer from 'components/Forms/FormSentContainer';
import FeedWrapper from "components/FeedWrapper/FeedWrapper";
import AllRoutes from "Routes/AllRoutes";
import { getUserInfo } from 'redux/users/actions';
import { currentSellerId } from 'constants/storageKeys';
import { ErrorBoundary } from 'components/Errors';
import {Loader} from "./pages";
import {sellersRequestErrorMessage} from "./constants/errors";
import {t} from "i18next";
import { userService } from "api";
import KeyboardShortcutsModal from 'components/KeyboardShortcutsModal/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from 'hooks';
import { setDarkMode } from 'redux/theme/reducer';
import { useTranslation } from 'react-i18next';
import { LanguageCurrencyModal } from 'components/RegonalSettings/RegionalSettings';
import { Theme } from 'components/ThemeSwitcher/ThemeSwitcher';
import { useUserSettings } from 'hooks/useUserSettings';

const memoizedSelector = createSelector(
  state => state.users.currentUser.userLoaded,
  state => state.users.currentUser.data,
  state => state.sellers.isAllSellersLoaded,
  state => state.sellers.allSellers,
  state => state.sellers.currentSeller,
  state => state.sellers.requestError,
  state => state.users.currentUser.registrationSteps,
  state => state.appOptions.basePath,
  (currentUserLoaded, currentUserData, isAllSellersLoaded, allSellers, currentSeller, sellersRequestError, registrationSteps, basePath) =>
  ({ currentUserLoaded, currentUserData, isAllSellersLoaded, allSellers, currentSeller, sellersRequestError, registrationSteps, basePath})
);

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCurrentSellerSetted, setIsCurrentSellerSetted] = useState(false)
  const { i18n } = useTranslation();
  const location = useLocation();
  const isFullBleedRoute = ['/stocks-fbs', '/stocks-fbo', '/stocks-export', '/stocks-reserve', '/warehouses'].some(
    route => location.pathname.includes(route)
  );

  const dispatch = useDispatch();

  const { 
    currentUserLoaded,
    currentUserData,
    isAllSellersLoaded,
    allSellers,
    currentSeller,
    sellersRequestError,
    registrationSteps,
    basePath
  } = useSelector(memoizedSelector);

  const { currentUserInfo, currentThemeSet, settings } = useAppSelector(({ users, theme }) => ({
    currentUserInfo: users.currentUser.info,
    currentThemeSet: theme.currentThemeSet,
    settings: users.currentUser.settings
  }));

  const { updateSettings } = useUserSettings()
  useKeyboardShortcuts()

  useEffect(() => {
    const currentUserId = Cookies.get("currentUserId");
    const refreshToken = Cookies.get("jwt_refresh")
    if(currentUserId && refreshToken) {
      dispatch(refresh({id: currentUserId, refreshToken: refreshToken}));
    } else {
      window.location.href = process.env.REACT_APP_AUTH_APP_ADD_ACCOUNT_URL;
    }
  }, []);

  useEffect(() => {
    // Get current user sellers
    if(currentUserData.accessToken && allSellers.length <= 0) {
      dispatch(getCurrentUserSellers({id: currentUserData.id}))
    }
  }, [currentUserData.accessToken, registrationSteps.data])

  useEffect(() => {
    if(currentUserLoaded) {
      const currentUserId = currentUserData?.id;
      if(currentUserId && currentUserData.accessToken){
        batch(() => {
          dispatch(getUserRegistrationSteps(currentUserId));
          dispatch(getUserInfo())
          dispatch(getUserSettings())
        })
      }
    }
  }, [currentUserData?.id, currentUserLoaded]);

  // Тема: применяем выбор пользователя и сохраняем в localStorage('theme') —
  // ключ также читает инлайн-скрипт в index.html (анти-FOUC) и слушатель системной темы
  useEffect(() => {
    const savedTheme = settings?.theme;
    if (!savedTheme) return;
    const mode = Object.values(Theme).includes(savedTheme) ? savedTheme : Theme.AUTO;
    localStorage.setItem('theme', mode);
    applyTheme(mode);

    const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      if (localStorage.getItem('theme') === Theme.AUTO) {
        applyTheme(e.matches ? Theme.DARK : Theme.LIGHT);
      }
    };

    prefersDarkScheme.addEventListener('change', handleSystemThemeChange);

    return () => {
      prefersDarkScheme.removeEventListener('change', handleSystemThemeChange);
    };
  }, [settings?.theme]);

  // Набор темы: атрибут data-theme-set на <html> управляет CSS-переменными --theme-*
  useEffect(() => {
    if (currentThemeSet?.id) document.documentElement.dataset.themeSet = currentThemeSet.id;
  }, [currentThemeSet?.id]);

  // Язык интерфейса
  useEffect(() => {
    if (settings?.language) i18n.changeLanguage(settings.language);
  }, [settings?.language]);

  // Сайдбар: применяем настройку один раз при первой загрузке,
  // чтобы смена других настроек не затирала ручное состояние
  const sidebarInitRef = useRef(false);
  useEffect(() => {
    if (!settings || sidebarInitRef.current) return;
    sidebarInitRef.current = true;
    setSidebarOpen(settings.sidebarOpen != null ? settings.sidebarOpen : window.innerWidth > 1024);
  }, [settings]);

  const applyTheme = (theme) => {
    const isDark = theme === Theme.DARK || (theme === Theme.AUTO && window.matchMedia('(prefers-color-scheme: dark)').matches);

    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    dispatch(setDarkMode(isDark));
  };

  useEffect(() => {
    if(currentUserInfo?.id) {
      // Observe chat restore
      window.addEventListener('storage', (event) => {
        if (event.key === 'session_id' && event.newValue) {
          userService.saveActualChatSessionId({
            userID: currentUserInfo.id,
            sessionId: event.newValue,
          });
          Cookies.set("chat_session_id", event.newValue, {path: '/', expires: 365});
        }
      })
      const chat_session_id = currentUserInfo?.chatSessionId || Cookies.get("chat_session_id");
      if (chat_session_id) {
        localStorage.setItem('session_id', chat_session_id);
      }
    }
  }, [currentUserInfo]);

  useEffect( () => {
    // set current seller
    if(
       currentUserData?.accessToken &&
       registrationSteps.data?.completeRegistration &&
       allSellers.length >= 0 &&
       !currentSeller.info.data?.id)
    {
      fillCurrentSeller()
    }
  }, [registrationSteps, currentUserData, allSellers]);

  const fillCurrentSeller = () => {
    const requestedSellerId = parseInt(window.location.pathname.replace(`${basePath}`, '').replaceAll("/", ""))
    const sellerIdFromStorage =  localStorage.getItem(currentSellerId);

    if(requestedSellerId && typeof requestedSellerId === "number") {
      dispatch(getAndSetSellerById(requestedSellerId));
    }else if(sellerIdFromStorage && registrationSteps.data?.acceptServiceAgreement) {
      dispatch(getAndSetSellerById(parseInt(sellerIdFromStorage)));
    } else {
      dispatch(getAndSetSellerById(allSellers[0]?.id))
    }
  }

  // Тема набора (colorSet) инициализируется в redux/theme из localStorage('selectedTheme')
  // при создании стора; серверное значение подхватывает getUserSettings → setThemeSet.

  useEffect(() => {
    const sellerId = currentSeller.info.data?.id;
    if(sellerId) {
      setIsCurrentSellerSetted(true)
    } else  {
      setIsCurrentSellerSetted(false)
    }
  }, [currentSeller.info.data]);

  const handleSave = (language, country, currency) => {
    i18n.changeLanguage(language)
    updateSettings({ language, currency })
    dispatch(setUserCurrency(currency));
  };

  const setSidebarOpenFn = (value) => {
    const newValue = typeof value === 'boolean' ? value : value(sidebarOpen)
    setSidebarOpen(newValue)
    updateSettings({ sidebarOpen: newValue }, true)
  }

  const saveShortcuts = (value) => {
    updateSettings({ shortcuts: value })
  }

  if (!settings) {
    return <Loader />
  }

  return (
    <div className={`
      flex h-app ${isFullBleedRoute ? 'overflow-hidden' : ''}
      [scrollbar-width:thin]
      [scrollbar-color:var(--theme-color)_var(--theme-bg)]

      /* Firefox - темная тема */
      dark:[scrollbar-color:var(--theme-color)_var(--theme-bg)]

      /* Базовые стили для всех элементов со скроллом - светлая тема */
      [&_[class*="overflow"]]:scrollbar-thin
      [&_[class*="overflow"]]:scrollbar-thumb-theme-color
      [&_[class*="overflow"]]:scrollbar-track-theme-bg
      [&_[class*="overflow"]]:hover:scrollbar-thumb-theme-hover
      [&_[class*="overflow"]]:active:scrollbar-thumb-theme-hover

      /* Базовые стили для темной темы */
      dark:[&_[class*="overflow"]]:scrollbar-thin
      dark:[&_[class*="overflow"]]:scrollbar-thumb-theme-color
      dark:[&_[class*="overflow"]]:scrollbar-track-theme-bg
      dark:[&_[class*="overflow"]]:hover:scrollbar-thumb-theme-hover
      dark:[&_[class*="overflow"]]:active:scrollbar-thumb-theme-hover
      
      /* Webkit стили - светлая тема */
      [&_[class*="overflow"]]::-webkit-scrollbar:w-2
      [&_[class*="overflow"]]::-webkit-scrollbar-thumb:bg-theme-color
      [&_[class*="overflow"]]::-webkit-scrollbar-track:bg-theme-bg
      [&_[class*="overflow"]]::-webkit-scrollbar-thumb:hover:bg-theme-hover
      [&_[class*="overflow"]]::-webkit-scrollbar-thumb:active:bg-theme-hover

      /* Webkit стили - темная тема */
      dark:[&_[class*="overflow"]]::-webkit-scrollbar:w-2
      dark:[&_[class*="overflow"]]::-webkit-scrollbar-thumb:bg-theme-color
      dark:[&_[class*="overflow"]]::-webkit-scrollbar-track:bg-theme-bg
      dark:[&_[class*="overflow"]]::-webkit-scrollbar-thumb:hover:bg-theme-hover
      dark:[&_[class*="overflow"]]::-webkit-scrollbar-thumb:active:bg-theme-hover
    `}>
      <AlertContainer />
      <FormSentContainer />
      <PostMessageCatcher />
      <FeedWrapper />
      <KeyboardShortcutsModal saveShortcuts={saveShortcuts} />
      {/* {currentUserLoaded && <SidebarOld sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />} */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpenFn} />
      <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpenFn} />
      <main id="content" className={`${sidebarOpen ? 'pl-50' : 'sm:pl-10.5'} lg:pt-10.5 maxh620:pt-8.5 w-full h-full max-sm:pb-[var(--mobile-bar-h)] max-sm:pl-0 pt-8.5`}>
        <div className={`${isFullBleedRoute ? '' : 'p-2 sm:p-5 md:pt-5 space-y-5'} relative h-full`}>
          {/* {currentUserLoaded && <HeaderOld sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />} */}
          <ErrorBoundary>
            {isAllSellersLoaded ?
                <AllRoutes
                    isAllSellersLoaded={isAllSellersLoaded}
                    isCurrentSellerSetted={isCurrentSellerSetted}
                    currentSeller={currentSeller?.info?.data}
                    currentUserData={currentUserData}
                    allSellers={allSellers}
                    currentUserLoaded={currentUserLoaded}
                    registrationSteps={registrationSteps}
                />
                :
                <Loader />
            }
            {sellersRequestError &&
                <div className="w-full flex items-center flex-col justify-center h-full max-w-[]">
                  <div className="font-bold text-red-500 text-center">{t(sellersRequestErrorMessage)}: {sellersRequestError}.</div>
                  <div className="mt-2 font-bold text-red-500 text-center">{t("Try refreshing the page")}</div>
                </div>
            }
          </ErrorBoundary>
        </div>
      </main>
      <LanguageCurrencyModal onSave={handleSave} />
    </div>
  );
}

export default App;